<template>
  <section class="section-padding section-bg" id="section_2">
    <div class="container">
      <div class="row">
        <div class="col-lg-6 col-12">
          <div class="custom-text-box">
            <!-- English legal text is shown for every locale; only the link label and page title are translated. -->
            <p><b>DIVERSE CARE NOW</b></p>
            <p><b>TERMS AND CONDITIONS</b></p>
            <p><b>Effective Date: October 31, 2026</b></p>

            <p><b>1. Acceptance of Terms</b></p>
            <p>By accessing the Diverse Care Now website, scheduling an appointment, purchasing services, or receiving medical care, patients acknowledge and agree to these Terms and Conditions.</p>
            <p>Diverse Care Now provides medical and wellness services through appropriately licensed healthcare professionals, subject to applicable federal and state laws.</p>

            <p><b>2. Medical Services</b></p>
            <p>Diverse Care Now offers services that may include:</p>
            <ul>
              <li>In-person and telemedicine consultations</li>
              <li>Medical weight-loss management</li>
              <li>Hormone replacement therapy</li>
              <li>Prescription medication management</li>
              <li>Vitamin injections and IV therapy</li>
              <li>Aesthetic and cosmetic procedures</li>
              <li>Laboratory testing and medical evaluations</li>
              <li>Other medically appropriate wellness services</li>
            </ul>
            <p>All treatments are subject to medical evaluation, provider discretion, and applicable legal requirements.</p>
            <p><b>No specific medical outcome, weight-loss result, or cosmetic improvement is guaranteed.</b></p>

            <p><b>3. Telemedicine Services</b></p>
            <p>Telemedicine allows patients to receive healthcare services remotely through secure communication technologies.</p>
            <p>Patients acknowledge that:</p>
            <ul>
              <li>Telemedicine may have limitations compared with in-person examinations.</li>
              <li>Providers may require laboratory testing or an in-person evaluation.</li>
              <li>Prescriptions are issued only when medically appropriate and legally permitted.</li>
              <li>Services are available only when the treating provider is appropriately authorized to practice in the state where the patient is physically located.</li>
              <li>Telemedicine is not intended for medical emergencies.</li>
            </ul>
            <p>In an emergency, patients should call 911 or seek immediate emergency medical attention.</p>

            <p><b>4. Prescription Medications and Compounded Products</b></p>
            <p>All prescription medications require an appropriate medical evaluation and valid prescription.</p>
            <p>Certain treatments may involve compounded medications prepared by appropriately licensed pharmacies.</p>
            <p>Patients understand that:</p>
            <ul>
              <li>Compounded medications are not FDA-approved.</li>
              <li>The FDA does not evaluate compounded medications for safety, effectiveness, or quality before marketing.</li>
              <li>Medication availability, pricing, and formulations may change.</li>
              <li>Prescriptions are issued at the provider's clinical discretion.</li>
              <li>Payment does not guarantee that a prescription will be issued.</li>
              <li>Compounded products are provided only when legally permitted and clinically appropriate.</li>
            </ul>
            <p>Diverse Care Now does not guarantee medication availability or specific treatment outcomes.</p>

            <p><b>5. Weight-Loss Programs</b></p>
            <p>Weight-loss results vary based on individual health conditions, treatment adherence, lifestyle, nutrition, and other factors.</p>
            <p>Patients acknowledge that medications may cause side effects, including nausea, vomiting, constipation, diarrhea, and other adverse reactions.</p>
            <p>Treatment may be adjusted, suspended, or discontinued when clinically indicated.</p>
            <p><b>Weight loss is not guaranteed.</b></p>

            <p><b>6. Hormone Therapy and Aesthetic Procedures</b></p>
            <p>Hormone therapy and aesthetic treatments require individualized medical evaluation.</p>
            <p>Patients understand that:</p>
            <ul>
              <li>All procedures and medications involve potential risks.</li>
              <li>Results vary between individuals.</li>
              <li>Additional treatments or follow-up visits may be necessary.</li>
              <li>Patients must follow pre-treatment and post-treatment instructions.</li>
              <li>Separate informed consent documents may be required before treatment.</li>
            </ul>

            <p><b>7. Payments and Fees</b></p>
            <p>Diverse Care Now operates primarily as a self-pay medical practice.</p>
            <ul>
              <li>Payment is generally required before or at the time services are rendered.</li>
              <li>Consultation fees, laboratory charges, medications, procedures, and shipping may be billed separately.</li>
              <li>Patients are responsible for charges disclosed and authorized before services are provided.</li>
              <li>Prices may change, but changes will not retroactively affect previously agreed charges.</li>
              <li>Recurring charges require separate authorization specifying the amount, frequency, and cancellation process.</li>
            </ul>

            <p><b>8. Cancellation and Refund Policy</b></p>
            <p>Appointments should be canceled or rescheduled at least 24 hours in advance whenever possible.</p>
            <ul>
              <li>Completed medical consultations and services are generally nonrefundable, except where required by law or where a billing error occurred.</li>
              <li>Completed aesthetic procedures are generally nonrefundable, except where required by law.</li>
              <li>Refund requests for services not yet provided are reviewed according to applicable law and the circumstances of the transaction.</li>
              <li>Prescription medication refunds depend on pharmacy policies, dispensing status, and applicable law.</li>
              <li>Patients will be informed of any applicable cancellation or no-show fee before it is charged.</li>
            </ul>
            <p>Diverse Care Now reserves the right to review refund requests individually.</p>

            <p><b>9. Shipping and Medication Delivery</b></p>
            <p>When medications are shipped through a pharmacy or authorized fulfillment provider:</p>
            <ul>
              <li>Delivery times may vary.</li>
              <li>Patients must provide accurate shipping information.</li>
              <li>Temperature-sensitive medications must be stored according to pharmacy instructions.</li>
              <li>Patients should report damaged, delayed, or improperly stored medications promptly.</li>
              <li>Shipping charges and replacement eligibility will be disclosed when applicable.</li>
            </ul>

            <p><b>10. Patient Responsibilities</b></p>
            <p>Patients agree to:</p>
            <ul>
              <li>Provide accurate medical history and contact information.</li>
              <li>Disclose medications, allergies, pregnancy status, and relevant medical conditions.</li>
              <li>Follow prescribed treatment instructions.</li>
              <li>Attend required follow-up appointments.</li>
              <li>Report side effects or complications promptly.</li>
              <li>Refrain from sharing prescribed medications with others.</li>
            </ul>
            <p>Failure to provide accurate information may affect treatment safety and eligibility.</p>

            <p><b>11. Privacy and Confidentiality</b></p>
            <p>Diverse Care Now is committed to protecting patient information in accordance with applicable privacy laws, including HIPAA where applicable.</p>
            <p>Protected health information is handled according to the practice's Notice of Privacy Practices.</p>
            <p>Patients may contact the clinic regarding privacy questions or requests involving their medical records.</p>

            <p><b>12. Communication and Electronic Consent</b></p>
            <p>Patients may receive appointment reminders, billing communications, and treatment-related messages through approved communication channels.</p>
            <p>Marketing communications and automated text messages will be sent only when legally permitted and with any required consent.</p>
            <p>Patients may withdraw optional communication consent in accordance with applicable law.</p>

            <p><b>13. Medical Disclaimer</b></p>
            <p>Information provided on the Diverse Care Now website, social media pages, or promotional materials is intended for general education and does not replace individualized medical advice.</p>
            <p>Treatment decisions are made by licensed healthcare professionals based on each patient's clinical circumstances.</p>

            <p><b>14. Limitation of Liability</b></p>
            <p>To the extent permitted by applicable law, Diverse Care Now is not responsible for interruptions in website availability or problems caused by third-party technology providers outside its reasonable control.</p>
            <p>Nothing in these Terms limits patient rights, professional responsibilities, or liability that cannot legally be excluded, including rights related to medical negligence.</p>

            <p><b>15. Changes to Terms</b></p>
            <p>Diverse Care Now may update these Terms and Conditions periodically.</p>
            <p>Updated terms will be posted with a revised effective date. Material changes requiring additional consent will be handled in accordance with applicable law.</p>

            <p><b>16. Contact Information</b></p>
            <p><b>Diverse Care Now</b></p>
            <p>Pompano Beach, Florida</p>
            <p>Website: www.diversecarenow.com</p>
            <p>For questions about appointments, payments, treatments, or these Terms and Conditions, patients may contact Diverse Care Now through its official website or patient communication channels.</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "TermsPage",
  computed: {
    pageTitle() {
      return `${this.$t('links.terms')} | Diverse Care Now`;
    }
  },
  watch: {
    pageTitle(title) {
      document.title = title;
    }
  },
  mounted() {
    this.previousTitle = document.title;
    document.title = this.pageTitle;
  },
  beforeUnmount() {
    document.title = this.previousTitle;
  }
};
</script>
